/* eslint semi: 1 */
import { defineStore } from 'pinia';
import _ from 'lodash';
import Utils, { Dependencies } from '@/globals/utils.js';
import { proxyException } from '@/utils/communication';
import { formatStateItem, TAG_TYPE_STATE_KEY } from './storeUtils.js';

export const tagsStore = defineStore('tags-store', {
  state: () => ({
    [TAG_TYPE_STATE_KEY.RESOURCE_TAGS]: [],
    [TAG_TYPE_STATE_KEY.LED_TAGS]: [],
    [TAG_TYPE_STATE_KEY.VIDEOWALL_TAGS]: [],
    [TAG_TYPE_STATE_KEY.PLAYER_TAGS]: [],
    tagSliderTagTypes: [],
    isFetching: false,
    error: null
  }),
  getters: {
    tagSliderTagCombinations (state) {
      const keys = state.tagSliderTagTypes;

      const tagCombinations = keys.reduce((acc, key) => {
        return { ...acc, [key]: state[key] };
      }, {});

      return tagCombinations;
    },
    sortedLedTags (state) {
      return Utils.sortTags({ tags: state[TAG_TYPE_STATE_KEY.LED_TAGS] ?? [] });
    },
    sortedVideowallTags (state) {
      return Utils.sortTags({ tags: state[TAG_TYPE_STATE_KEY.VIDEOWALL_TAGS] ?? [] });
    }
  },
  actions: ({
    /**
     * This function is commonly used to add tags in the state based on a specific state key
     * @param {Array} tags - The tags to be added to state
     * @param {String} stateKey - The property name of the state. e.g. resourceTags, ledTags, videowallTags, playerTags
     */
    commonAddTags (stateKey, tags) {
      if (_.isEmpty(stateKey) || !_.isArray(tags)) {
        return;
      }

      const newTags = [];

      /**
       * Format each tag as a state item and filter to ensure it is not already present in the current list.
       */

      const newTagsToAdd = _.chain(tags)
        .map(formatStateItem)
        .compact()
        .filter(tag => !_.some(this[stateKey], { name: tag?.name }))
        .value();

      if (!_.isEmpty(newTagsToAdd) && _.isArray(newTagsToAdd)) {
        newTags.push(...newTagsToAdd);
      }

      this[stateKey] = newTags;
    },
    /**
     * Remove tags from the state based on the specified state key.
     * @param {String} stateKey - The key of the state to update.
     * @param {Array} tags - The tags to remove.
     */
    commonRemoveTags (stateKey, tags) {
      if (!_.isArray(tags)) return;

      this[stateKey] = _.differenceBy(this[stateKey], tags, 'name');
    },
    commonUpdateTag (stateKey, oldTagValue, newTagValue) {
      const currentTags = this[stateKey];
      const newState = currentTags.map((tag) => {
        if (tag.name === oldTagValue) {
          tag.name = newTagValue;
        }

        return tag;
      });

      this[stateKey] = newState;
    },
    addResourceTags (tags) {
      this.commonAddTags(TAG_TYPE_STATE_KEY.RESOURCE_TAGS, tags);
    },
    addLEDTags (tags) {
      this.commonAddTags(
        TAG_TYPE_STATE_KEY.LED_TAGS,
        _.map(tags, t => ({
          ...Utils.parseLedTag(t.name),
          color: t?.color,
          inline: t?.inline ?? []
        }))
      );
    },
    addVideowallTags (tags) {
      this.commonAddTags(
        TAG_TYPE_STATE_KEY.VIDEOWALL_TAGS,
        _.map(tags, t => ({
          ...Utils.parseLedTag(t.name),
          color: t?.color,
          inline: t?.inline ?? [],
          bezels: _.isArray(t.bezels) ? t.bezels : undefined
        }))
      );
    },
    addPlayerTags (tags) {
      this.commonAddTags(TAG_TYPE_STATE_KEY.PLAYER_TAGS, tags);
    },
    removeResourceTags (tags) {
      this.commonRemoveTags(TAG_TYPE_STATE_KEY.RESOURCE_TAGS, tags);
    },
    removeLEDTags (tags) {
      this.commonRemoveTags(TAG_TYPE_STATE_KEY.LED_TAGS, tags);
    },
    removeVideowallTags (tags) {
      this.commonRemoveTags(TAG_TYPE_STATE_KEY.VIDEOWALL_TAGS, tags);
    },
    removePlayerTags (tags) {
      this.commonRemoveTags(TAG_TYPE_STATE_KEY.PLAYER_TAGS, tags);
    },
    /**
     * Store tag types combination displayed in slider
     * @param {Array} types
     */
    saveTagSliderTagTypes (types) {
      this.tagSliderTagTypes = types;
    },

    /**
     * Refresh list of tags from the server (main data source)
     */
    async fetchTags (types) {
      const $api = Dependencies.inject('$api');
      const paramTypes = types ? types.join(',') : _.values(TAG_TYPE_STATE_KEY).join(',');

      this.$patch(state => {
        state.isFetching = true;
        state.error = null;
      });

      const { ok: response, error } = await proxyException(() => $api.get(`${CONFIG.API_BASE_URL}/group/tags`, {
        params: {
          type: paramTypes,
          inline: 1,
          ext: 1
        }
      }));

      this.clearState(types ?? _.values(TAG_TYPE_STATE_KEY));

      if (error) {
        const errorMessage = error.response ? error.response?.data.message ?? error.message : 'Failed to fetch user tags';
        this.$patch(state => {
          state.isFetching = false;
          state.error = errorMessage;
        });
        return;
      }

      const responseData = response?.data ?? {};

      if (_.isArray(responseData)) {
        const stateKey = types[0] ?? null;
        switch (stateKey) {
          case TAG_TYPE_STATE_KEY.RESOURCE_TAGS:
            this.addResourceTags(responseData);
            break;
          case (TAG_TYPE_STATE_KEY.LED_TAGS):
            this.addLEDTags(responseData);
            break;
          case (TAG_TYPE_STATE_KEY.VIDEOWALL_TAGS):
            this.addVideowallTags(responseData);
            break;
          case (TAG_TYPE_STATE_KEY.PLAYER_TAGS):
            this.addPlayerTags(responseData);
            break;
        }
      } else {
        if (responseData.resourceTags) {
          this.addResourceTags(responseData.resourceTags);
        }
        if (responseData.ledTags) {
          this.addLEDTags(responseData.ledTags);
        }
        if (responseData.videowallTags) {
          this.addVideowallTags(responseData.videowallTags);
        }
        if (responseData.playerTags) {
          this.addPlayerTags(responseData.playerTags);
        }
      }

      this.isFetching = false;
    },
    async updateTags (oldTagValue, newTagValue, type) {
      const $api = Dependencies.inject('$api');
      const { error } = await proxyException(() => $api
        .put(`${CONFIG.API_BASE_URL}/group/tags/${encodeURIComponent(oldTagValue)}`, {
          name: encodeURIComponent(newTagValue),
          type
        })
      );

      if (error) {
        const errorMessage = error.response ? error.response?.data.message ?? error.message : 'Failed to update tags';
        this.$patch(state => {
          state.error = errorMessage;
        });

        return;
      }

      if (type === 'player') {
        this.commonUpdateTag(TAG_TYPE_STATE_KEY.PLAYER_TAGS, oldTagValue, newTagValue);
      } else {
        this.commonUpdateTag(TAG_TYPE_STATE_KEY.RESOURCE_TAGS, oldTagValue, newTagValue);
      }
    },
    async deleteTag (tagToDelete, type) {
      const $api = Dependencies.inject('$api');
      let typeParam = type;

      if (type === 'videowall') {
        typeParam = 'led';
      }

      const { error } = await proxyException(() => $api.delete(`${CONFIG.API_BASE_URL}/group/tags/${encodeURIComponent(tagToDelete.value)}`, {
        params: {
          type: typeParam
        }
      }));

      if (error) {
        const errorMessage = error.response ? error.response?.data.message ?? error.message : 'Failed to delete tag';
        this.$patch(state => {
          state.error = errorMessage;
        });

        return;
      }

      if (type === 'player') {
        this.removePlayerTags([tagToDelete]);
      } else if (type === 'led') {
        this.removeLEDTags([tagToDelete]);
      } else if (type === 'videowall') {
        this.removeVideowallTags([tagToDelete]);
      } else {
        this.removeResourceTags([tagToDelete]);
      }
    },
    async createTag (payload) {
      const $api = Dependencies.inject('$api');
      const { error } = await proxyException(() => $api.post(`${CONFIG.API_BASE_URL}/group/tags`, payload));

      if (error) {
        const errorMessage = error.response ? error.response?.data.message ?? error.message : 'Failed to create tag';
        this.$patch(state => {
          state.error = errorMessage;
        });
      }
    },
    clearState (stateKeys) {
      const patchToState = stateKeys.reduce((acc, key) => {
        return { ...acc, [key]: [] };
      }, {});

      this.$patch({
        ...patchToState,
        isFetching: false,
        error: null
      });
    }
  })
});
